import React, { useState } from "react";
import { BsBank2 } from "react-icons/bs";
import { FaCreditCard } from "react-icons/fa";
import { IoRepeatOutline } from "react-icons/io5";

const COLORS = {
    darkText: "#333",
    secondaryText: "#666",
    tertiaryText: "#999",
    border: "#E0E0E0",
    cardBg: "#fff",
    increase: "#e74c3c",
    stable: "#27ae60",
};

const sectionStyle = {
    marginBottom: "28px",
    maxWidth: "100%",
    overflow: "hidden",
};

const headerStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px",
    direction: "rtl",
    fontSize: "18px",
    fontWeight: "bold",
    color: COLORS.darkText,
};

const tableStyle = {
    width: "100%",
    background: COLORS.cardBg,
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    overflow: "hidden",
    direction: "rtl",
    borderCollapse: "separate",
    borderSpacing: 0,
};

const thStyle = {
    padding: "12px 18px",
    fontSize: "14px",
    fontWeight: "bold",
    color: COLORS.tertiaryText,
    borderBottom: `2px solid ${COLORS.border}`,
    textAlign: "right",
    background: "#fafafa",
};

const tdStyle = {
    padding: "12px 18px",
    fontSize: "15px",
    color: COLORS.darkText,
    borderBottom: `1px solid #f5f5f5`,
    textAlign: "right",
};

const diffStyle = (diff) => ({
    fontSize: "13px",
    fontWeight: "bold",
    padding: "2px 8px",
    borderRadius: "10px",
    display: "inline-block",
    background: diff > 0 ? "#ffeef0" : diff < 0 ? "#eefff5" : "#f5f5f5",
    color: diff > 0 ? COLORS.increase : diff < 0 ? COLORS.stable : COLORS.tertiaryText,
});

const accountIconStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: COLORS.secondaryText,
    fontSize: "14px",
};

const totalRowStyle = {
    padding: "14px 18px",
    fontSize: "16px",
    fontWeight: "bold",
    color: COLORS.darkText,
    borderTop: `2px solid ${COLORS.border}`,
    textAlign: "right",
    background: "#fafafa",
};

const mobileListStyle = {
    background: COLORS.cardBg,
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    overflow: "hidden",
    direction: "rtl",
};

const mobileCardStyle = {
    padding: "10px 14px",
    borderBottom: `1px solid #f3f3f3`,
};

const mobileCardTopRow = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "4px",
};

const mobileCardDescription = {
    fontSize: "14px",
    fontWeight: "600",
    color: COLORS.darkText,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    marginLeft: "10px",
};

const mobileCardAmount = {
    fontSize: "14px",
    fontWeight: "bold",
    color: COLORS.darkText,
    whiteSpace: "nowrap",
};

const mobileCardBottomRow = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
};

const mobileCardCategory = {
    fontSize: "11px",
    color: COLORS.secondaryText,
    background: "#f0f0f0",
    padding: "1px 8px",
    borderRadius: "6px",
};

const mobileCardMeta = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11px",
    color: COLORS.tertiaryText,
};

const mobileCardDate = {
    fontSize: "11px",
    color: COLORS.tertiaryText,
};

const mobileTotalRow = {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 14px",
    fontSize: "14px",
    fontWeight: "bold",
    color: COLORS.darkText,
    borderTop: `2px solid ${COLORS.border}`,
    background: "#fafafa",
};

function Tooltip({ text, children }) {
    const [show, setShow] = useState(false);
    if (!text) return children;
    return (
        <span
            style={{ position: "relative", cursor: "default" }}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            {children}
            {show && (
                <span style={{
                    position: "absolute",
                    bottom: "calc(100% + 6px)",
                    right: 0,
                    background: "#333",
                    color: "#fff",
                    padding: "5px 10px",
                    borderRadius: "6px",
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                    zIndex: 10,
                    pointerEvents: "none",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                }}>
                    {text}
                </span>
            )}
        </span>
    );
}

export default function RecurringExpenses({ items, isMobile = false }) {
    const [expanded, setExpanded] = useState(false);

    if (!items || items.length === 0) return null;

    const total = items.reduce((sum, item) => sum + item.amount, 0);
    const changedCount = items.filter((item) => item.diffInPercentage !== 0).length;

    const mobileThStyle = { ...thStyle, padding: "8px 8px", fontSize: "11px" };
    const mobileTdStyle = { ...tdStyle, padding: "8px 8px", fontSize: "12px" };
    const th = isMobile ? mobileThStyle : thStyle;
    const td = isMobile ? mobileTdStyle : tdStyle;

    return (
        <div style={sectionStyle}>
            <div
                style={{ ...headerStyle, cursor: "pointer", userSelect: "none" }}
                onClick={() => setExpanded(!expanded)}
            >
                <IoRepeatOutline size={20} color="#58508d" />
                הוצאות קבועות
                <span style={{ fontSize: "14px", fontWeight: "normal", color: COLORS.secondaryText }}>
                    ({items.length} פריטים{changedCount > 0 ? `, ${changedCount} עם שינוי` : ""})
                </span>
                <span style={{ fontSize: "14px", fontWeight: "bold", color: COLORS.darkText, marginRight: "auto" }}>
                    {formatAmount(total)}₪
                </span>
                <span style={{ fontSize: "14px", color: COLORS.tertiaryText, transition: "transform 0.2s", display: "inline-block", transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}>
                    ▼
                </span>
            </div>

            {expanded && (
                isMobile ? (
                    <div style={mobileListStyle}>
                        {items.map((item) => (
                            <div key={`${item.id}-${item.accountType}`} style={mobileCardStyle}>
                                <div style={mobileCardTopRow}>
                                    <span style={mobileCardDescription}>
                                        <Tooltip text={item.recurringName !== item.description ? item.recurringName : null}>
                                            {item.description}
                                        </Tooltip>
                                    </span>
                                    <span style={mobileCardAmount}>{formatAmount(item.amount)}₪</span>
                                </div>
                                <div style={mobileCardBottomRow}>
                                    <span style={mobileCardCategory}>{item.category}</span>
                                    <div style={mobileCardMeta}>
                                        {item.accountType === "bank" ? <BsBank2 size={11} color={COLORS.tertiaryText} /> : <FaCreditCard size={11} color={COLORS.tertiaryText} />}
                                        <span style={mobileCardDate}>{item.date}</span>
                                        {item.diffInPercentage !== 0 ? (
                                            <span style={diffStyle(item.diffInPercentage)}>
                                                {item.diffInPercentage > 0 ? "+" : ""}{item.diffInPercentage}%
                                            </span>
                                        ) : (
                                            <span style={diffStyle(0)}>ללא שינוי</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div style={mobileTotalRow}>
                            <span>סה״כ</span>
                            <span style={{ fontWeight: "bold" }}>{formatAmount(total)}₪</span>
                        </div>
                    </div>
                ) : (
                    <div style={{ overflowX: "visible", maxWidth: "100%" }}>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={th}>תיאור</th>
                                    <th style={th}>קטגוריה</th>
                                    <th style={th}>חשבון</th>
                                    <th style={th}>יום בחודש</th>
                                    <th style={th}>סכום</th>
                                    <th style={th}>שינוי מממוצע</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr key={`${item.id}-${item.accountType}`}>
                                        <td style={{ ...td, fontWeight: "600" }}>
                                            <Tooltip text={item.recurringName !== item.description ? item.recurringName : null}>
                                                {item.description}
                                            </Tooltip>
                                        </td>
                                        <td style={td}>
                                            <span style={{ fontSize: "13px", color: COLORS.secondaryText, background: "#f0f0f0", padding: "2px 8px", borderRadius: "8px" }}>
                                                {item.category}
                                            </span>
                                        </td>
                                        <td style={td}>
                                            <div style={accountIconStyle}>
                                                {item.accountType === "bank" ? <BsBank2 size={14} /> : <FaCreditCard size={14} />}
                                                {item.accountType === "bank" ? "בנק" : "אשראי"}
                                            </div>
                                        </td>
                                        <td style={td}>{item.date}</td>
                                        <td style={{ ...td, fontWeight: "bold" }}>{formatAmount(item.amount)}₪</td>
                                        <td style={td}>
                                            {item.diffInPercentage !== 0 ? (
                                                <span style={diffStyle(item.diffInPercentage)}>
                                                    {item.diffInPercentage > 0 ? "+" : ""}{item.diffInPercentage}%
                                                </span>
                                            ) : (
                                                <span style={diffStyle(0)}>ללא שינוי</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td style={totalRowStyle} colSpan={4}>סה״כ הוצאות קבועות</td>
                                    <td style={totalRowStyle}>{formatAmount(total)}₪</td>
                                    <td style={totalRowStyle}></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )
            )}
        </div>
    );
}

function formatAmount(amount) {
    return Number(amount).toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
